<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate, toApiDateTime } from '@yuksalish/ui'
import { useHrApi } from '../composables/useHrApi'
import EmployeePickerModal from '../components/EmployeePickerModal.vue'
import {
  attendanceStatusClass,
  attendanceStatusLabel,
  formatMinutesAsHours,
  toDateInput,
} from '../utils/format'
import type { AttendanceRecord, AttendanceStatus, Employee, Option } from '../types/hr'

const { t } = useI18n()
const auth = useAuthStore()
const route = useRoute()
const {
  listAttendance,
  listAllAttendance,
  createAttendance,
  updateAttendance,
  deleteAttendance,
  getEmployee,
  listWorkplaceOptions,
} = useHrApi()

const permissions = computed(() => auth.user?.permissions ?? [])
const canManage = computed(() => permissions.value.includes('attendance.manage'))

function daysAgo(days: number): string {
  const date = new Date()
  date.setDate(date.getDate() - days)
  return toDateInput(date)
}

const rows = ref<AttendanceRecord[]>([])
const totals = ref<AttendanceRecord[]>([])
const departments = ref<Option[]>([])
const loading = ref(true)
const error = ref('')
const actionError = ref('')
const actionMessage = ref('')
const submitting = ref(false)

const filters = ref({
  search: '',
  department_agent_id: '' as string | number,
  status: '',
  date_from: daysAgo(6),
  date_to: toDateInput(new Date()),
})

const page = ref(1)
const totalPages = ref(1)
const total = ref(0)

const showForm = ref(false)
const showEmployeePicker = ref(false)
const editingId = ref<number | null>(null)
const selectedEmployee = ref<Employee | null>(null)

const form = reactive({
  employee_id: null as number | null,
  attendance_date: toDateInput(new Date()),
  status: 'present' as AttendanceStatus,
  check_in: '',
  check_out: '',
  worked_minutes: '' as number | string,
  notes: '',
})

let searchTimer: ReturnType<typeof setTimeout> | undefined

function buildFilters() {
  return {
    search: filters.value.search.trim() || undefined,
    department_agent_id: filters.value.department_agent_id || undefined,
    status: filters.value.status || undefined,
    date_from: filters.value.date_from || undefined,
    date_to: filters.value.date_to || undefined,
  }
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await listAttendance({ ...buildFilters(), page: page.value, per_page: 20 })
    rows.value = result.items
    total.value = result.meta.total
    totalPages.value = Math.max(1, result.meta.total_pages)
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function loadTotals() {
  try {
    totals.value = await listAllAttendance(buildFilters())
  } catch {
    totals.value = []
  }
}

async function loadDepartments() {
  try {
    departments.value = await listWorkplaceOptions()
  } catch {
    departments.value = []
  }
}

function refresh() {
  void load()
  void loadTotals()
}

onMounted(() => {
  void loadDepartments()
  refresh()
  void applyQueryPrefill()
})

watch(
  () => [filters.value.department_agent_id, filters.value.status, filters.value.date_from, filters.value.date_to],
  () => {
    page.value = 1
    refresh()
  },
)

watch(
  () => filters.value.search,
  () => {
    if (searchTimer) clearTimeout(searchTimer)
    searchTimer = setTimeout(() => {
      page.value = 1
      refresh()
    }, 300)
  },
)

const statusCounts = computed(() => {
  const counts: Record<string, number> = {}
  for (const record of totals.value) {
    counts[record.status] = (counts[record.status] ?? 0) + 1
  }
  return counts
})

function goToPage(next: number) {
  page.value = Math.min(Math.max(1, next), totalPages.value)
  void load()
}

function openCreate() {
  actionError.value = ''
  actionMessage.value = ''
  editingId.value = null
  selectedEmployee.value = null
  form.employee_id = null
  form.attendance_date = filters.value.date_to || toDateInput(new Date())
  form.status = 'present'
  form.check_in = ''
  form.check_out = ''
  form.worked_minutes = ''
  form.notes = ''
  showForm.value = true
}

function openEdit(record: AttendanceRecord) {
  actionError.value = ''
  actionMessage.value = ''
  editingId.value = record.id
  selectedEmployee.value = {
    id: record.employee_id,
    employee_code: record.employee_code,
    first_name: record.first_name,
    last_name: record.last_name,
  } as Employee
  form.employee_id = record.employee_id
  form.attendance_date = record.attendance_date.slice(0, 10)
  form.status = record.status
  form.check_in = record.check_in_at ? record.check_in_at.slice(11, 16) : ''
  form.check_out = record.check_out_at ? record.check_out_at.slice(11, 16) : ''
  form.worked_minutes = record.worked_minutes
  form.notes = record.notes ?? ''
  showForm.value = true
}

/**
 * Deep link from the employee profile: opens the create form with the employee
 * already selected (`/attendance?employee=42&action=new`).
 */
async function applyQueryPrefill() {
  const employeeId = Number(route.query.employee ?? '')
  if (!employeeId || route.query.action !== 'new' || !canManage.value) return
  openCreate()
  try {
    const employee = await getEmployee(employeeId)
    selectedEmployee.value = employee
    form.employee_id = employee.id
  } catch {
    // Keep the form open without a preselected employee.
  }
}

function selectEmployee(employee: Employee) {
  selectedEmployee.value = employee
  form.employee_id = employee.id
  showEmployeePicker.value = false
}

async function submit() {
  actionError.value = ''
  actionMessage.value = ''
  if (!form.employee_id) {
    actionError.value = t('hr.attendance.invalidEmployee')
    return
  }
  if (!form.attendance_date) {
    actionError.value = t('hr.attendance.invalidDate')
    return
  }

  const payload = {
    employee_id: form.employee_id,
    attendance_date: form.attendance_date,
    status: form.status,
    check_in_at: form.check_in ? toApiDateTime(`${form.attendance_date} ${form.check_in}`) : null,
    check_out_at: form.check_out ? toApiDateTime(`${form.attendance_date} ${form.check_out}`) : null,
    worked_minutes: form.worked_minutes === '' ? 0 : Number(form.worked_minutes),
    notes: form.notes.trim() || null,
  }

  submitting.value = true
  try {
    if (editingId.value === null) {
      await createAttendance(payload)
      actionMessage.value = t('hr.attendance.created')
    } else {
      await updateAttendance(editingId.value, payload)
      actionMessage.value = t('hr.attendance.updated')
    }
    showForm.value = false
    refresh()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

async function remove(record: AttendanceRecord) {
  actionError.value = ''
  actionMessage.value = ''
  if (!window.confirm(t('hr.attendance.deleteConfirm'))) return
  try {
    await deleteAttendance(record.id)
    actionMessage.value = t('hr.attendance.deleted')
    if (rows.value.length === 1 && page.value > 1) page.value -= 1
    refresh()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  }
}

function employeeName(employee: Employee): string {
  const value = `${employee.last_name ?? ''} ${employee.first_name ?? ''}`.trim()
  return value || employee.employee_code
}
</script>

<template>
  <div class="page-heading">
    <h2>{{ t('hr.attendance.title') }}</h2>
  </div>

  <div class="toolbar">
    <input v-model="filters.search" class="search-input" type="search" :placeholder="t('hr.common.searchPlaceholder')">
    <select v-model="filters.department_agent_id" class="filter-select">
      <option value="">{{ t('hr.common.allDepartments') }}</option>
      <option v-for="department in departments" :key="department.id" :value="department.id">{{ department.name }}</option>
    </select>
    <select v-model="filters.status" class="filter-select">
      <option value="">{{ t('hr.common.allStatuses') }}</option>
      <option value="present">{{ t('hr.attendanceStatus.present') }}</option>
      <option value="absent">{{ t('hr.attendanceStatus.absent') }}</option>
      <option value="late">{{ t('hr.attendanceStatus.late') }}</option>
      <option value="leave">{{ t('hr.attendanceStatus.leave') }}</option>
      <option value="remote">{{ t('hr.attendanceStatus.remote') }}</option>
      <option value="half_day">{{ t('hr.attendanceStatus.half_day') }}</option>
    </select>
    <input v-model="filters.date_from" class="date-input" type="date">
    <input v-model="filters.date_to" class="date-input" type="date">
    <span class="spacer" />
    <button v-if="canManage" class="btn-primary" type="button" @click="openCreate">
      {{ t('hr.attendance.newRecord') }}
    </button>
  </div>

  <div class="summary-row">
    <div class="summary-card">
      <span class="summary-value">{{ statusCounts.present ?? 0 }}</span>
      <span class="summary-label">{{ t('hr.attendance.summaryPresent') }}</span>
    </div>
    <div class="summary-card attention">
      <span class="summary-value">{{ statusCounts.absent ?? 0 }}</span>
      <span class="summary-label">{{ t('hr.attendance.summaryAbsent') }}</span>
    </div>
    <div class="summary-card">
      <span class="summary-value">{{ statusCounts.late ?? 0 }}</span>
      <span class="summary-label">{{ t('hr.attendance.summaryLate') }}</span>
    </div>
    <div class="summary-card">
      <span class="summary-value">{{ statusCounts.leave ?? 0 }}</span>
      <span class="summary-label">{{ t('hr.attendance.summaryLeave') }}</span>
    </div>
    <div class="summary-card">
      <span class="summary-value">{{ statusCounts.remote ?? 0 }}</span>
      <span class="summary-label">{{ t('hr.attendance.summaryRemote') }}</span>
    </div>
    <div class="summary-card">
      <span class="summary-value">{{ statusCounts.half_day ?? 0 }}</span>
      <span class="summary-label">{{ t('hr.attendance.summaryHalfDay') }}</span>
    </div>
  </div>

  <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>
  <p v-if="actionError" class="error-inline">{{ actionError }}</p>

  <div v-if="loading" class="state-message"><p>{{ t('hr.attendance.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="refresh">{{ t('hr.common.retry') }}</button>
  </div>

  <div v-else-if="rows.length === 0" class="state-message"><p>{{ t('hr.attendance.empty') }}</p></div>

  <template v-else>
    <div class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('hr.attendance.date') }}</th>
            <th>{{ t('hr.attendance.employee') }}</th>
            <th>{{ t('hr.attendance.department') }}</th>
            <th>{{ t('hr.attendance.status') }}</th>
            <th>{{ t('hr.attendance.checkIn') }}</th>
            <th>{{ t('hr.attendance.checkOut') }}</th>
            <th>{{ t('hr.attendance.worked') }}</th>
            <th>{{ t('hr.attendance.notes') }}</th>
            <th v-if="canManage" class="cell-actions">{{ t('hr.common.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="record in rows" :key="record.id">
            <td class="cell-date">{{ formatDisplayDate(record.attendance_date) }}</td>
            <td class="cell-name">
              {{ record.full_name }}
              <small class="cell-sub">{{ record.employee_code }}</small>
            </td>
            <td>{{ record.department_name || '—' }}</td>
            <td>
              <span :class="['status-badge', attendanceStatusClass(record.status)]">
                {{ attendanceStatusLabel(t, record.status) }}
              </span>
            </td>
            <td class="cell-date">{{ record.check_in_at ? record.check_in_at.slice(11, 16) : '—' }}</td>
            <td class="cell-date">{{ record.check_out_at ? record.check_out_at.slice(11, 16) : '—' }}</td>
            <td class="cell-num">{{ formatMinutesAsHours(record.worked_minutes) }}</td>
            <td>{{ record.notes || '—' }}</td>
            <td v-if="canManage" class="cell-actions">
              <button class="link-action" type="button" @click="openEdit(record)">{{ t('hr.attendance.edit') }}</button>
              &nbsp;·&nbsp;
              <button class="link-action danger" type="button" @click="remove(record)">{{ t('hr.attendance.delete') }}</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pagination">
      <span>{{ t('hr.attendance.totals', { count: total }) }} · {{ t('hr.common.page', { page, total: Math.max(totalPages, 1) }) }}</span>
      <button type="button" :disabled="page <= 1" @click="goToPage(page - 1)">{{ t('hr.common.previous') }}</button>
      <button type="button" :disabled="page >= totalPages" @click="goToPage(page + 1)">{{ t('hr.common.next') }}</button>
    </div>
  </template>

  <div v-if="showForm" class="modal-overlay" @click.self="showForm = false">
    <form class="modal" @submit.prevent="submit">
      <h3>{{ editingId === null ? t('hr.attendance.formTitleCreate') : t('hr.attendance.formTitleEdit') }}</h3>
      <div class="form-grid tight">
        <div class="field field-wide">
          <label>{{ t('hr.attendance.employee') }}</label>
          <button class="field-button" type="button" @click="showEmployeePicker = true">
            <span :class="{ placeholder: !selectedEmployee }">
              {{ selectedEmployee ? employeeName(selectedEmployee) : t('hr.attendance.invalidEmployee') }}
            </span>
            <span aria-hidden="true">▾</span>
          </button>
        </div>
        <div class="field">
          <label for="attendance-date">{{ t('hr.attendance.date') }}</label>
          <input id="attendance-date" v-model="form.attendance_date" type="date" required>
        </div>
        <div class="field">
          <label for="attendance-status">{{ t('hr.attendance.status') }}</label>
          <select id="attendance-status" v-model="form.status">
            <option value="present">{{ t('hr.attendanceStatus.present') }}</option>
            <option value="absent">{{ t('hr.attendanceStatus.absent') }}</option>
            <option value="late">{{ t('hr.attendanceStatus.late') }}</option>
            <option value="leave">{{ t('hr.attendanceStatus.leave') }}</option>
            <option value="remote">{{ t('hr.attendanceStatus.remote') }}</option>
            <option value="half_day">{{ t('hr.attendanceStatus.half_day') }}</option>
          </select>
        </div>
        <div class="field">
          <label for="attendance-check-in">{{ t('hr.attendance.checkIn') }}</label>
          <input id="attendance-check-in" v-model="form.check_in" type="time">
        </div>
        <div class="field">
          <label for="attendance-check-out">{{ t('hr.attendance.checkOut') }}</label>
          <input id="attendance-check-out" v-model="form.check_out" type="time">
        </div>
        <div class="field">
          <label for="attendance-minutes">{{ t('hr.attendance.minutes') }}</label>
          <input id="attendance-minutes" v-model="form.worked_minutes" type="number" min="0" step="1">
          <small>{{ t('hr.attendance.minutesHint') }}</small>
        </div>
        <div class="field field-wide">
          <label for="attendance-notes">{{ t('hr.attendance.notes') }}</label>
          <textarea id="attendance-notes" v-model="form.notes" />
        </div>
      </div>

      <p v-if="actionError" class="error-inline">{{ actionError }}</p>

      <div class="modal-actions">
        <button class="btn-secondary" type="button" @click="showForm = false">{{ t('hr.common.cancel') }}</button>
        <button class="btn-primary" type="submit" :disabled="submitting">
          {{ submitting ? t('hr.common.saving') : t('hr.common.save') }}
        </button>
      </div>
    </form>
  </div>

  <EmployeePickerModal
    :open="showEmployeePicker"
    @close="showEmployeePicker = false"
    @select="selectEmployee"
  />
</template>
